import { CountResponse } from '@interfaces/common.interface';
import {
  CountPatientsRequestParams,
  CreatePatientRequestBody,
  GetPatientsRequestParams,
  Patient,
  UpdatePatientRequest,
} from '@interfaces/patients.interface';
import {
  GetTherapiesByPatientIdRequest,
  Therapy,
} from '@interfaces/therapies.interface';

import api from './';
import {
  GetPainSpikesDailyCountByPatientIdRequest,
  GetPainSpikesRequest,
  PainSpike,
  PainSpikeDailyCount,
} from '@interfaces/painSpikes.interface';
import {
  GetTerraPayloadsRequest,
  TerraPayload,
} from '@interfaces/terra.interface';

export const {
  useGetPatientByIdQuery,
  useCountPatientsQuery,
  useGetManyPatientsQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useCountTherapiesByPatientIdQuery,
  useGetTherapiesByPatientIdQuery,
  useGetPainSpikesByPatientIdQuery,
  useGetPainSpikesDailyCountByPatientIdQuery,
  useGetTerraPayloadsByPatientIdQuery,
} = api.injectEndpoints({
  endpoints: (build) => ({
    getPatientById: build.query<Patient, string>({
      query: (id) => ({ url: `patients/${id}` }),
      providesTags: ['patients'],
    }),
    countPatients: build.query<CountResponse, CountPatientsRequestParams>({
      query: (params) => ({ url: 'patients/count', params }),
      providesTags: ['patients'],
    }),
    getManyPatients: build.query<Patient[], GetPatientsRequestParams>({
      query: (params) => ({ url: 'patients', params }),
      providesTags: ['patients'],
    }),
    createPatient: build.mutation<Patient, CreatePatientRequestBody>({
      query: (body) => ({
        url: 'patients',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['patients'],
    }),
    updatePatient: build.mutation<Patient, UpdatePatientRequest>({
      query: ({ id, body }) => ({
        url: `patients/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['patients'],
    }),
    deletePatient: build.mutation<Patient, string>({
      query: (id) => ({
        url: `patients/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['patients'],
    }),
    countTherapiesByPatientId: build.query<CountResponse, string>({
      query: (id) => ({ url: `patients/${id}/therapies/count` }),
      providesTags: ['therapies'],
    }),
    getTherapiesByPatientId: build.query<
      Therapy[],
      GetTherapiesByPatientIdRequest
    >({
      query: ({ patientId, params }) => ({
        url: `patients/${patientId}/therapies`,
        params,
      }),
      providesTags: ['therapies'],
    }),
    getPainSpikesByPatientId: build.query<PainSpike[], GetPainSpikesRequest>({
      query: ({ patientId, params }) => ({
        url: `patients/${patientId}/pain-spikes`,
        params,
      }),
      providesTags: ['painSpikes'],
    }),
    getPainSpikesDailyCountByPatientId: build.query<
      PainSpikeDailyCount[],
      GetPainSpikesDailyCountByPatientIdRequest
    >({
      query: ({ patientId, params }) => ({
        url: `patients/${patientId}/pain-spikes/daily-count`,
        params,
      }),
      providesTags: ['painSpikes'],
    }),
    getTerraPayloadsByPatientId: build.query<
      TerraPayload,
      GetTerraPayloadsRequest
    >({
      query: ({ patientId, params }) => ({
        url: `patients/${patientId}/terra-payloads`,
        params,
      }),
      providesTags: ['terraPayloads'],
    }),
  }),
});
